'use client';

import { useLocale } from '@akademi/site-builder-runtime';
import en from './en.json';
import tr from './tr.json';

/**
 * This theme's own strings, looked up from its own catalogue.
 *
 * <p>WHY NOT `useTranslation(NS)`: the storefront's i18next instance is built from a fixed list
 * of catalogues (`BlocksI18nProvider`: the akademi blocks, commerce's blocks, the sorsy theme)
 * and has no way for an extension's theme to add one. So `theme-solar-pv` was never loaded,
 * and every lookup returned its key — the live home page showed `home.incVat`, `home.add` and
 * `quick.submit` on 27 September 2026, found by the first screenshot anyone took of it. The
 * unit tests could not see it: without a provider i18next returns keys there too.
 *
 * <p>Reading the JSON directly needs nothing from the host and cannot come back as a key: an
 * unknown language falls back to English, and an unknown key to its English string. When the
 * host grows a way to register extension catalogues, this can go back to `useTranslation`.
 */
type Catalogue = Record<string, unknown>;
const CATALOGUES: Record<string, Catalogue> = { en, tr };

function lookup(catalogue: Catalogue, key: string): string | undefined {
  let node: unknown = catalogue;
  for (const part of key.split('.')) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

export function solarText(language: string) {
  const catalogue = CATALOGUES[language] ?? CATALOGUES.en!;
  return (key: string, values?: Record<string, string | number>): string => {
    const template = lookup(catalogue, key) ?? lookup(CATALOGUES.en!, key) ?? key;
    return values ? template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => String(values[name] ?? '')) : template;
  };
}

/** The theme's translator for the shop's current language. */
export function useSolarText() {
  const { locale } = useLocale();
  return solarText((locale || 'en').split('-')[0]!.toLowerCase());
}
